import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeftIcon, RefreshCcwIcon } from 'lucide-react';
import {
  BadgeStatus,
  Button,
  LinkedinAdsIcon,
  LinkedinAdsTrackingCallout,
} from '@tinct/web-components';
import { LayoutContent, LayoutHeader, LayoutTopbar } from '@tinct/web-layout';
import { Collection, type UseQueryTable } from '@tinct/web-collection';
import { ReadAdsCampaign } from '@tinct/core-api-models';
import {
  useGetAdsCampaigns,
  useGetCrmIntegrations,
  useTriggerCrmSync,
} from '@tinct/core-api';
import {
  useCurrentWorkspaceUrlPrefix,
  useGetCurrentWorkspaceId,
} from '@tinct/core-workspace';
import { useAdsCampaignsColumns } from '../google-ads-integration-page/hooks/use-ads-campaigns-columns';
import { useOAuthPopup } from '../edit-integrations-page/hooks/use-oauth-popup';
import { getLinkedinTokenState } from '../../components/linkedin-ads-token-state';
import { ReauthorizationBanner } from './components/reauthorization-banner';
import { AdAccountPicker } from './components/ad-account-picker';
import { CampaignMetricsPanel } from './components/campaign-metrics-panel';

// How long the ads-campaigns list keeps fast-polling after "Sync now" — the
// trigger endpoint returns no job id to key on, so a bounded window is the
// pragmatic signal (rows refresh as the sync lands).
const SYNC_POLL_WINDOW_MS = 30_000;
const SYNC_POLL_INTERVAL_MS = 5000;

/** Collection adapter: staticParams → the hook's `integrationId` param. */
const useAdsCampaignsTable: UseQueryTable<ReadAdsCampaign, { integrationId: string }> = (
  params,
  options,
) => {
  const { staticParams, ...rest } = params;
  return useGetAdsCampaigns(
    { ...rest, integrationId: staticParams?.integrationId ?? '' },
    options,
  );
};

export function LinkedinAdsIntegrationPage() {
  const { integrationId = '' } = useParams<{ integrationId: string }>();
  const workspaceId = useGetCurrentWorkspaceId();
  const workspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();

  const { data: integrations } = useGetCrmIntegrations(
    {
      offset: 0,
      limit: 50,
      fields: {
        id: null,
        status: null,
        connectedAt: null,
        createdAt: null,
        errorDetails: null,
        lastSyncAt: null,
        ownerId: null,
        ownerType: null,
        providerType: null,
        settings: null,
        tokenExpiresAt: null,
        needsReauthorization: null,
        updatedAt: null,
      },
      filters: { organizationId: workspaceId ?? '' }
    },
    { enabled: !!workspaceId },
  );
  const integration = useMemo(
    () => (integrations?.data ?? []).find((i) => i.id === integrationId),
    [integrations, integrationId],
  );
  const tokenState = getLinkedinTokenState(integration);

  // Reconnect = the very same OAuth flow as the first connection.
  const { openOAuthPopup, isPending: isReconnecting } = useOAuthPopup({
    organizationId: workspaceId,
    provider: 'linkedin_ads',
  });

  const [syncingUntil, setSyncingUntil] = useState<number | null>(null);
  const isSyncWindowOpen = syncingUntil !== null;
  useEffect(() => {
    if (syncingUntil === null) return;
    const timeout = setTimeout(
      () => setSyncingUntil(null),
      Math.max(0, syncingUntil - Date.now()),
    );
    return () => clearTimeout(timeout);
  }, [syncingUntil]);

  const triggerSync = useTriggerCrmSync({
    onSuccess: () => {
      toast.success('Sync started — campaigns and metrics refresh as it lands');
      setSyncingUntil(Date.now() + SYNC_POLL_WINDOW_MS);
    },
    onError: (error) => toast.error(error.error),
  });
  const syncNow = () => triggerSync.mutate({ integrationId });

  const [selectedCampaign, setSelectedCampaign] = useState<ReadAdsCampaign | null>(null);

  const columns = useAdsCampaignsColumns({
    basePath: workspaceUrlPrefix,
    provider: 'linkedin_ads',
  });

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar />
      </header>
      <LayoutContent>
        <LayoutHeader
          pageTitle="LinkedIn Ads"
          pageDescription="LinkedIn campaigns synced from your ad accounts, the page variants matched to them, and their daily metrics"
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" asChild>
                <Link to={`${workspaceUrlPrefix}/settings/integrations`}>
                  <ArrowLeftIcon className="size-4" />
                  Integrations
                </Link>
              </Button>
              <Button
                onClick={syncNow}
                disabled={triggerSync.isPending || isSyncWindowOpen}
                isLoading={triggerSync.isPending}
              >
                {!triggerSync.isPending && <RefreshCcwIcon className="size-4" />}
                {isSyncWindowOpen ? 'Syncing…' : 'Sync now'}
              </Button>
            </div>
          }
        />

        <div className="flex flex-col gap-6">
          <ReauthorizationBanner
            tokenState={tokenState}
            onReconnect={openOAuthPopup}
            isReconnecting={isReconnecting}
          />

          {/* Connection status. Variant generation is opt-in per Tinct
              campaign (an ads campaign), not an integration-level setting. */}
          <div className="flex flex-wrap items-center gap-6 rounded-xl border border-border bg-card px-5 py-4">
            <div className="flex items-center gap-3">
              <LinkedinAdsIcon width={28} height={28} />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  LinkedIn Ads
                </span>
                {tokenState.kind === 'expired' ? (
                  <BadgeStatus variant="red">Reconnect required</BadgeStatus>
                ) : integration?.status === 'ready' ? (
                  <BadgeStatus variant="green">Connected</BadgeStatus>
                ) : integration?.status === 'error' ? (
                  <BadgeStatus variant="red">Error</BadgeStatus>
                ) : (
                  <BadgeStatus variant="yellow">Pending</BadgeStatus>
                )}
              </div>
            </div>
            {integration?.errorDetails && integration.status === 'error' && (
              <p className="max-w-xl text-sm text-muted-foreground">
                {integration.errorDetails}
              </p>
            )}
          </div>

          <AdAccountPicker integrationId={integrationId} onRequestSync={syncNow} />

          {/* Without the tracking parameter no click carries the campaign id,
              and no variant is ever served — worth repeating on every visit. */}
          <LinkedinAdsTrackingCallout />

          {/* Ads campaigns — pick a row to see its daily metrics below. */}
          <Collection
            useLocationAsState
            columns={columns}
            useQueryFn={useAdsCampaignsTable}
            staticParams={{ integrationId }}
            refetchInterval={isSyncWindowOpen ? SYNC_POLL_INTERVAL_MS : undefined}
            onRowClick={(row) => setSelectedCampaign(row)}
            queryFields={{
              id: null,
              name: null,
              externalCampaignId: null,
              advertisingChannelType: null,
              status: null,
              finalUrls: null,
              matchedCampaignId: null,
              matchNormalizedUrl: null,
              lastSyncedAt: null,
            }}
            emptyState={
              <div className="flex flex-col items-center justify-center gap-1 py-16 text-center">
                <p className="text-sm font-medium text-foreground">No LinkedIn campaigns yet</p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  Running campaigns from the selected ad accounts appear here
                  after the first sync.
                </p>
              </div>
            }
          />

          {selectedCampaign?.id ? (
            <CampaignMetricsPanel
              key={selectedCampaign.id}
              integrationId={integrationId}
              campaign={selectedCampaign}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Select a campaign in the table to see its daily metrics.
            </p>
          )}
        </div>
      </LayoutContent>
    </div>
  );
}
